import { TableData, TableSort } from 'app/components/table/tabledata';
import { CurrentUser } from 'app/components/data/core/models/base/user';
import { OrderStatus } from 'app/components/data/core/models/order/order';
import { PFQuery } from 'app/components/data/core/models/base/query';
import * as AccessQueries from 'app/components/access/query';
import { TranslateService } from '@ngx-translate/core';
import { QueryResultFilter } from 'app/components/access/queryResultFilter';
import { LiveQuerySubscriber, LiveQueryModifier, LiveQueryInclusion } from 'app/components/table/data_sources/live-query-subscriber';

import { OrderQuerySearchFilter } from '../../helpers/order-query-search-filter';
import { TreeNode } from "../../../../components/hierarchical-checkbox/tree-node.model";
import { OrderColumns } from "../../helpers/order-columns-factory";
import { ServerV2TableDataSource } from "../../../../components/table/data_sources/server-v2-data-source";
import { TableNameKeys } from "../../../../components/table/data_sources/table-name-keys";

export class OrdersListTableData extends TableData {

  constructor(private translate: TranslateService) {
    super();

    let columns = [OrderColumns.makeColumnNumber()];
    let userColumns;

    if (CurrentUser.isCustomer()) {
      userColumns = [
        OrderColumns.makeColumnUnloadPoint(),
        OrderColumns.makeColumnBrand(),
        OrderColumns.makeColumnTonnage(),
        OrderColumns.makeColumnUnloadDate(this.translate.currentLang),
        OrderColumns.makeColumnVehicleNumber(),
        OrderColumns.makeColumnCustomerStatus(this.translate),
      ];
    } else {
      userColumns = [
        OrderColumns.makeColumnCustomer(),
        OrderColumns.makeColumnUnloadDate(this.translate.currentLang),
        OrderColumns.makeColumnUnloadPoint(),
        OrderColumns.makeColumnTonnage(),
        OrderColumns.makeColumnCreatedAt(this.translate.currentLang),
        OrderColumns.makeColumnVehicleNumber(),
        OrderColumns.makeColumnDriverStatus(this.translate),
        OrderColumns.makeColumnEquipment(this.translate),
      ];

      if (CurrentUser.isAdministrator() || CurrentUser.isLogistician()) {
        userColumns.push(OrderColumns.makeColumnAuthorName());
      }
    }
    userColumns.push(OrderColumns.makeColumnComment());
    this.setColumns(columns.concat(userColumns));

    let includeFields = [
      'unloadingPoint',
      'offers.trip.historyRecords',     
      'offers.trip.transportUnit.driver',
      'offers.trip.transportUnit.vehicle',
      'offers.trip.transportUnit.user',
      'customer',
      'loadingEntrance',
      'unloadingEntrance',
      'carriers',
      'carrierOffers.driver',
    ];


    if (CurrentUser.isCustomer()) {
      includeFields.push('unloadingPoint.manager');
    } else {
      includeFields = includeFields.concat(['loadingPoint', 'intermediary', 'supplier', 'loadingEntrance', 'loadingAgreement']);
    }

    if (CurrentUser.isAdministrator() || CurrentUser.isLogistician()) {
      includeFields = includeFields.concat(['author', 'author.manager', 'author.dispatcher', 'author.customer', 'author.logistician']);
    }

    let sort: TableSort = new TableSort('number', 'desc');
    let tableDataSource = new ServerV2TableDataSource('Order', includeFields);

    tableDataSource.setDefaultSort(sort);


    tableDataSource.queryFilter = (filter, query) => {
      let searchQuery;
      let unloadingEntranceAddressFilter = OrderQuerySearchFilter.unloadingEntranceAddress(filter);
      
      let driverNameFilter = OrderQuerySearchFilter.driverName(filter); 
      let vehicleNumberFilter = OrderQuerySearchFilter.vehicleNumber(filter);

      if (CurrentUser.isCustomer()) {
        let brandNameFilter = OrderQuerySearchFilter.brandName(filter);
        searchQuery = PFQuery.or(
          brandNameFilter,
          unloadingEntranceAddressFilter,
          driverNameFilter,     
          vehicleNumberFilter   
        );
        let tonnage = parseFloat(filter);
        if (!isNaN(tonnage)) {
          searchQuery = PFQuery.or(searchQuery, OrderQuerySearchFilter.expectedTonnage(tonnage));
        }
      } else {
        let customerNameFilter = OrderQuerySearchFilter.customerName(filter);
        searchQuery = PFQuery.or(
          customerNameFilter,
          unloadingEntranceAddressFilter,
          driverNameFilter,      
          vehicleNumberFilter 
        );
      }

      let orderNum = parseFloat(filter);
      if (!isNaN(orderNum)) {
        searchQuery = PFQuery.or(searchQuery, OrderQuerySearchFilter.orderNumber(orderNum));
      }
      return searchQuery;
    };

    const applyQueryMod = (q: PFQuery) => {
      q = AccessQueries.filterDeleted(q);
      if (CurrentUser.isDispatcher()) {
        q = AccessQueries.containsCurrentCarrier(q);
      } else if (!CurrentUser.isAdministrator() && !CurrentUser.isLogistician() && !CurrentUser.isCustomer()) {
        q = AccessQueries.authorMatchesCurrentUser(q);
        q = PFQuery.or(q, AccessQueries.managerMatchesCurrentUser(new PFQuery('Order')));
      }
      q.greaterThan('undistributedTonnage', 0);
      if (CurrentUser.isCustomer()) {
        q.equalTo('customer', CurrentUser.customer());
      } else {
        q.equalTo('status', OrderStatus.READY);
        q.exists('customer');
      }
      return q;
    };

    tableDataSource.queryModify = (q) => applyQueryMod(q);
    let subscriber = new LiveQuerySubscriber('Order', includeFields);
    subscriber.liveQueryModifier = new LiveQueryModifier((q) => applyQueryMod(q));

    let filterFn = (obj) => {
      if (CurrentUser.isDispatcher()) {
        if (!QueryResultFilter.transportUnitMatchesCurrentCarrier(obj.transportUnit())) { return null; }
      } else if (!CurrentUser.isAdministrator() && !CurrentUser.isLogistician()) {
        if (!QueryResultFilter.orderAuthorMatchesCurrentUser(obj.transportUnit())) { return null; }
      }
      return obj;
    };

    subscriber.liveQueryInclusions = [
      new LiveQueryInclusion("Offer", new LiveQueryModifier(null, filterFn)),
      new LiveQueryInclusion("Trip", new LiveQueryModifier(null, filterFn), ["historyRecords"]),
    ];

    tableDataSource.setLiveQuerySubscriber(subscriber);
    this.setProvider(tableDataSource);
  }

  canSortColumn(column) {
    return [TableNameKeys.number, TableNameKeys.createdAt, TableNameKeys.tonnage, TableNameKeys.unloadDate, TableNameKeys.customer].includes(column.key as any);
  }

  canSortByDateColumn(column) {
    return ![TableNameKeys.unloadDate, TableNameKeys.createdAt, TableNameKeys.customer, TableNameKeys.author, TableNameKeys.driverStatus].includes(column.key as any);
  }

  getStatusFilterData() {
    return ['Принят', 'Не принят', 'В очереди', 'Отказался', 'Согласован', 'Не согласован'].map(name => ({ name, checked: false }));
  }
}
